import { EntryRow } from './EntryRow.js';
import type { IntelGroup } from '../model/intel.js';
import { DATA_LABEL } from '../../theme/type.js';

/**
 * One category's entries, collapsible (design 470–723).
 *
 * **The collapse unit is the group, and the row is not a second one.** A row that
 * also folded would put an entry's fields behind a click nobody finds — and with
 * two to four fields on a typical entry, the click would save one line and cost
 * the operator the thing they opened the report to read.
 *
 * The heading is the projection's own `category` word, verbatim. A display table
 * over nine values that are already English would be a second vocabulary for the
 * ORDER / MISSION / DETECTION list to drift out of agreement with, and there is
 * nothing here for it to fix — unlike `GAME_EVENT_TYPES`, where fifty-four enum
 * names are exactly what an operator must not be shown.
 */

export interface EntryGroupProps {
  readonly group: IntelGroup;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onSelect: (id: string) => void;
}

export function EntryGroup(props: EntryGroupProps): JSX.Element {
  const { group } = props;
  return (
    <section data-testid="intel-group" data-category={group.category} data-open={props.open}>
      <button
        type="button"
        data-testid="intel-group-header"
        aria-expanded={props.open}
        onClick={props.onToggle}
        style={{
          ...DATA_LABEL,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 27,
          padding: '0 12px',
          background: 'var(--surface-2)',
          border: 'none',
          borderBottom: '1px solid var(--border)',
          color: 'var(--text-2)',
          fontSize: 10.5,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span style={{ flex: 'none', width: 8, color: 'var(--text-muted)' }}>
          {props.open ? '▾' : '▸'}
        </span>
        <span style={{ flex: 'none' }}>{group.category}</span>
        {group.criticalCount > 0 && (
          <span
            data-testid="intel-group-critical"
            style={{ flex: 'none', color: 'var(--red-text)' }}
          >
            {group.criticalCount} CRITICAL
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span data-testid="intel-group-count" style={{ flex: 'none', color: 'var(--text-muted)' }}>
          {group.rows.length}
        </span>
      </button>

      {props.open &&
        group.rows.map((row) => <EntryRow key={row.key} row={row} onSelect={props.onSelect} />)}
    </section>
  );
}
